import React, { useState, useEffect, useId } from 'react';
import {
  ShieldCheck,
  Users,
  Mail,
  Activity,
  Key,
  LogOut,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Lock,
  Send,
  Database,
  Eye,
  EyeOff,
  Search,
  ArrowLeft,
  Server,
  FileText
} from 'lucide-react';
import { getSessionEvents, clearSessionEvents, TrackedEvent } from '../utils/analytics';

interface AdminDashboardProps {
  onNavigateHome: () => void;
}

export default function AdminDashboard({ onNavigateHome }: AdminDashboardProps) {
  const [token, setToken] = useState<string>(() => {
    return sessionStorage.getItem('zipytiny_admin_token') || '';
  });
  const [adminUser, setAdminUser] = useState<string>('');
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Login form state
  const [usernameInput, setUsernameInput] = useState<string>('admin');
  const [passwordInput, setPasswordInput] = useState<string>('');
  const [mfaInput, setMfaInput] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Active Tab: 'overview' | 'users' | 'support' | 'analytics' | 'audit' | 'settings'
  const [activeTab, setActiveTab] = useState<string>('users');

  // Data states
  const [googleUsers, setGoogleUsers] = useState<any[]>([]);
  const [supportMessages, setSupportMessages] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [ipLockouts, setIpLockouts] = useState<any[]>([]);
  const [sessionEvents, setSessionEvents] = useState<TrackedEvent[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  // Search & Filter
  const [userSearch, setUserSearch] = useState<string>('');
  const [supportFilter, setSupportFilter] = useState<'all' | 'pending' | 'resolved'>('all');

  // Settings & Diagnostics
  const [dbDiagResult, setDbDiagResult] = useState<any>(null);
  const [dbDiagLoading, setDbDiagLoading] = useState<boolean>(false);
  const [testEmailInput, setTestEmailInput] = useState<string>('');
  const [testEmailStatus, setTestEmailStatus] = useState<string | null>(null);
  const [testEmailLoading, setTestEmailLoading] = useState<boolean>(false);

  // New credentials form
  const [newAdminUser, setNewAdminUser] = useState<string>('');
  const [newAdminPass, setNewAdminPass] = useState<string>('');
  const [credSaveStatus, setCredSaveStatus] = useState<string | null>(null);

  const usernameId = useId();
  const passwordId = useId();
  const mfaId = useId();

  // Verify token on mount if stored
  useEffect(() => {
    if (token) {
      fetch('/api/admin/verify-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      })
        .then(res => res.json())
        .then(data => {
          if (!data.valid) {
            handleLogout();
          } else {
            loadDashboardData(token);
          }
        })
        .catch(() => {
          handleLogout();
        });
    }
  }, [token]);

  // Load analytics live events from memory
  useEffect(() => {
    setSessionEvents(getSessionEvents());
    const handleDispatched = () => {
      setSessionEvents(getSessionEvents());
    };
    window.addEventListener('ga-event-dispatched', handleDispatched);
    return () => window.removeEventListener('ga-event-dispatched', handleDispatched);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);

    try {
      const res = await fetch('/api/admin/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: usernameInput.trim(),
          password: passwordInput.trim(),
          mfaCode: mfaInput.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Authentication failed. Please verify credentials.');
      }

      const receivedToken = data.token;
      sessionStorage.setItem('zipytiny_admin_token', receivedToken);
      setToken(receivedToken);
      setAdminUser(data.user || 'admin');
      loadDashboardData(receivedToken);
    } catch (err: any) {
      setAuthError(err.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    if (token) {
      try {
        await fetch('/api/admin/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
      } catch {
        // Ignored
      }
    }
    sessionStorage.removeItem('zipytiny_admin_token');
    setToken('');
    setAdminUser('');
  };

  const loadDashboardData = async (activeToken: string) => {
    setDataLoading(true);
    try {
      // 1. Fetch Google Users
      const usersRes = await fetch('/api/admin/google-users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: activeToken })
      });
      if (usersRes.ok) {
        const uData = await usersRes.json();
        setGoogleUsers(uData.users || []);
      }

      // 2. Fetch Support Tickets
      const supportRes = await fetch('/api/admin/support-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: activeToken })
      });
      if (supportRes.ok) {
        const sData = await supportRes.json();
        setSupportMessages(sData.messages || []);
      }

      // 3. Fetch Audit Logs
      const auditRes = await fetch('/api/admin/audit-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: activeToken })
      });
      if (auditRes.ok) {
        const aData = await auditRes.json();
        setAuditLogs(aData.logs || []);
      }

      // 4. Fetch IP Tracker
      const ipRes = await fetch('/api/admin/ip-tracker', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: activeToken })
      });
      if (ipRes.ok) {
        const ipData = await ipRes.json();
        setIpLockouts(ipData.lockouts || []);
      }
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const handleResolveTicket = async (ticketId: string) => {
    try {
      const res = await fetch('/api/admin/support-messages/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ticketId })
      });
      if (res.ok) {
        setSupportMessages(prev =>
          prev.map(m => (m.ticketId === ticketId ? { ...m, status: 'resolved' } : m))
        );
      }
    } catch (err) {
      console.error('Failed to resolve ticket:', err);
    }
  };

  const handleRunDbDiag = async () => {
    setDbDiagLoading(true);
    setDbDiagResult(null);
    try {
      const res = await fetch('/api/admin/db-diagnostic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      setDbDiagResult(data);
    } catch (err: any) {
      setDbDiagResult({ success: false, error: err.message });
    } finally {
      setDbDiagLoading(false);
    }
  };

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmailInput.trim()) return;
    setTestEmailLoading(true);
    setTestEmailStatus(null);
    try {
      const res = await fetch('/api/admin/test-smtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, testEmail: testEmailInput.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setTestEmailStatus('Test email dispatched successfully! Check inbox/spam.');
      } else {
        setTestEmailStatus(`Error: ${data.error || 'Failed to dispatch test email.'}`);
      }
    } catch (err: any) {
      setTestEmailStatus(`Error: ${err.message}`);
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleSaveCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAdminUser.trim() || !newAdminPass.trim()) return;
    setCredSaveStatus(null);
    try {
      const res = await fetch('/api/admin/save-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          adminUser: newAdminUser.trim(),
          adminPassword: newAdminPass.trim()
        })
      });
      const data = await res.json();
      if (res.ok) {
        setCredSaveStatus('Admin credentials successfully updated in database.');
        setNewAdminUser('');
        setNewAdminPass('');
      } else {
        setCredSaveStatus(`Error: ${data.error || 'Failed to update credentials'}`);
      }
    } catch (err: any) {
      setCredSaveStatus(`Error: ${err.message}`);
    }
  };

  // If not logged in, render the login form
  if (!token) {
    return (
      <div className="min-h-screen bg-neutral-900 text-white flex flex-col justify-center items-center px-4 py-12">
        <div className="w-full max-w-md bg-neutral-800/90 border border-neutral-700 rounded-3xl p-8 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight">Zipytiny Admin</h1>
                <p className="text-xs text-neutral-400">Security & Operational Console</p>
              </div>
            </div>
            <button
              onClick={onNavigateHome}
              className="p-2 rounded-xl text-neutral-400 hover:text-white hover:bg-neutral-700 transition"
              title="Return to home"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>

          {authError && (
            <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400" />
              <span>{authError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor={usernameId} className="block text-xs font-semibold text-neutral-300 mb-1.5">
                Admin Username
              </label>
              <input
                id={usernameId}
                type="text"
                value={usernameInput}
                onChange={e => setUsernameInput(e.target.value)}
                placeholder="admin"
                className="w-full px-4 py-3 rounded-xl bg-neutral-900 border border-neutral-700 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                required
              />
            </div>

            <div>
              <label htmlFor={passwordId} className="block text-xs font-semibold text-neutral-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id={passwordId}
                  type={showPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={e => setPasswordInput(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-900 border border-neutral-700 text-sm text-white focus:outline-none focus:border-blue-500 transition pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-neutral-400 hover:text-neutral-200"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor={mfaId} className="block text-xs font-semibold text-neutral-300 mb-1.5">
                2FA Authenticator Code <span className="text-neutral-500 font-normal">(if configured)</span>
              </label>
              <input
                id={mfaId}
                type="text"
                value={mfaInput}
                onChange={e => setMfaInput(e.target.value)}
                placeholder="Leave blank if 2FA is not enabled"
                className="w-full px-4 py-3 rounded-xl bg-neutral-900 border border-neutral-700 text-sm text-white focus:outline-none focus:border-blue-500 transition"
              />
            </div>

            <button
              type="submit"
              disabled={authLoading}
              className="w-full mt-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {authLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  <span>Sign In to Admin Console</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-neutral-700/50 text-center">
            <p className="text-[11px] text-neutral-400">
              Default system credentials from environment: <code className="bg-neutral-900 px-1.5 py-0.5 rounded text-neutral-300">admin</code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Filtered Google Users
  const filteredUsers = googleUsers.filter(u => {
    if (!userSearch.trim()) return true;
    const term = userSearch.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(term) ||
      (u.displayName || '').toLowerCase().includes(term) ||
      (u.uid || '').toLowerCase().includes(term)
    );
  });

  // Filtered Support Messages
  const filteredSupport = supportMessages.filter(m => {
    if (supportFilter === 'all') return true;
    if (supportFilter === 'pending') return m.status !== 'resolved';
    if (supportFilter === 'resolved') return m.status === 'resolved';
    return true;
  });

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col">
      {/* Top Admin Navigation Header */}
      <header className="border-b border-neutral-800 bg-neutral-900/80 backdrop-blur-md sticky top-0 z-40 px-6 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onNavigateHome}
            className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Exit to Site</span>
          </button>
          <div className="h-4 w-px bg-neutral-700" />
          <div className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-bold tracking-tight text-white">Zipytiny Control Center</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
              Admin Session Active
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => loadDashboardData(token)}
            disabled={dataLoading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition"
            title="Refresh dashboard data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${dataLoading ? 'animate-spin' : ''}`} />
            <span>Sync</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 transition"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Body */}
      <div className="flex-1 flex flex-col md:flex-row max-w-7xl w-full mx-auto p-6 gap-6">
        {/* Left Vertical Navigation Menu */}
        <aside className="w-full md:w-60 shrink-0 space-y-1">
          <button
            onClick={() => setActiveTab('users')}
            className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                : 'text-neutral-400 hover:bg-neutral-800/80 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <Users className="w-4 h-4" />
              <span>Registered Users</span>
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 font-mono">
              {googleUsers.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('support')}
            className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'support'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                : 'text-neutral-400 hover:bg-neutral-800/80 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <Mail className="w-4 h-4" />
              <span>Support & Contact</span>
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 font-mono">
              {supportMessages.filter(m => m.status !== 'resolved').length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'analytics'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                : 'text-neutral-400 hover:bg-neutral-800/80 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <Activity className="w-4 h-4" />
              <span>GA4 & Telemetry</span>
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
              Live
            </span>
          </button>

          <button
            onClick={() => setActiveTab('audit')}
            className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'audit'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                : 'text-neutral-400 hover:bg-neutral-800/80 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="w-4 h-4" />
              <span>Security Audit</span>
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 font-mono">
              {auditLogs.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'settings'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                : 'text-neutral-400 hover:bg-neutral-800/80 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <Server className="w-4 h-4" />
              <span>Diagnostics & System</span>
            </div>
          </button>
        </aside>

        {/* Content Area */}
        <main className="flex-1 min-w-0">
          {/* TAB 1: USERS */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-neutral-900/60 p-4 rounded-2xl border border-neutral-800">
                <div>
                  <h2 className="text-base font-bold text-white">Google Authenticated Users</h2>
                  <p className="text-xs text-neutral-400">
                    Accounts stored securely in Firestore via Google Single Sign-On
                  </p>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-neutral-500" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search name, email, or UID..."
                    className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-neutral-950 border border-neutral-700 text-xs text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {filteredUsers.length === 0 ? (
                <div className="p-12 text-center bg-neutral-900/40 rounded-2xl border border-neutral-800/80">
                  <Users className="w-8 h-8 mx-auto text-neutral-600 mb-2" />
                  <p className="text-sm font-semibold text-neutral-300">No users found</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    When visitors sign in via Google Auth, their profile record appears here automatically.
                  </p>
                </div>
              ) : (
                <div className="bg-neutral-900/60 rounded-2xl border border-neutral-800 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="border-b border-neutral-800 bg-neutral-900/90 text-neutral-400">
                        <tr>
                          <th className="p-3.5 font-semibold">User</th>
                          <th className="p-3.5 font-semibold">Email</th>
                          <th className="p-3.5 font-semibold">Last Active</th>
                          <th className="p-3.5 font-semibold">First Joined</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-800/60">
                        {filteredUsers.map((u, i) => (
                          <tr key={u.uid || i} className="hover:bg-neutral-800/30 transition">
                            <td className="p-3.5 flex items-center gap-3">
                              {u.photoURL ? (
                                <img
                                  src={u.photoURL}
                                  alt={u.displayName}
                                  className="w-7 h-7 rounded-full object-cover border border-neutral-700"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-blue-600/30 text-blue-400 flex items-center justify-center font-bold text-xs">
                                  {(u.displayName || u.email || 'U').charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <p className="font-semibold text-white">
                                  {u.displayName || 'Anonymous User'}
                                </p>
                                <p className="text-[10px] font-mono text-neutral-500">{u.uid}</p>
                              </div>
                            </td>
                            <td className="p-3.5 text-neutral-300 font-mono text-[11px]">{u.email}</td>
                            <td className="p-3.5 text-neutral-400 text-[11px]">
                              {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'N/A'}
                            </td>
                            <td className="p-3.5 text-neutral-400 text-[11px]">
                              {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: SUPPORT TICKETS */}
          {activeTab === 'support' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-neutral-900/60 p-4 rounded-2xl border border-neutral-800">
                <div>
                  <h2 className="text-base font-bold text-white">Support Inquiries & Feedback</h2>
                  <p className="text-xs text-neutral-400">
                    User messages submitted via the contact form and chat widget
                  </p>
                </div>
                <div className="flex gap-1.5 bg-neutral-950 p-1 rounded-xl border border-neutral-800">
                  <button
                    onClick={() => setSupportFilter('all')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                      supportFilter === 'all' ? 'bg-neutral-800 text-white' : 'text-neutral-400'
                    }`}
                  >
                    All ({supportMessages.length})
                  </button>
                  <button
                    onClick={() => setSupportFilter('pending')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                      supportFilter === 'pending' ? 'bg-amber-500/20 text-amber-300' : 'text-neutral-400'
                    }`}
                  >
                    Pending ({supportMessages.filter(m => m.status !== 'resolved').length})
                  </button>
                  <button
                    onClick={() => setSupportFilter('resolved')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                      supportFilter === 'resolved' ? 'bg-emerald-500/20 text-emerald-300' : 'text-neutral-400'
                    }`}
                  >
                    Resolved ({supportMessages.filter(m => m.status === 'resolved').length})
                  </button>
                </div>
              </div>

              {filteredSupport.length === 0 ? (
                <div className="p-12 text-center bg-neutral-900/40 rounded-2xl border border-neutral-800/80">
                  <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm font-semibold text-neutral-300">No support tickets found</p>
                  <p className="text-xs text-neutral-500 mt-1">Your inbox is clear.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSupport.map(item => (
                    <div
                      key={item.ticketId || item.id}
                      className="bg-neutral-900/60 p-4 rounded-2xl border border-neutral-800 space-y-2.5"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              item.status === 'resolved'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                            }`}
                          >
                            {item.status === 'resolved' ? 'RESOLVED' : 'PENDING'}
                          </span>
                          <span className="text-xs font-bold text-white">{item.subject || 'Inquiry'}</span>
                          <span className="text-[10px] font-mono text-neutral-500">#{item.ticketId}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] text-neutral-500">
                            {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                          </span>
                          {item.status !== 'resolved' && (
                            <button
                              onClick={() => handleResolveTicket(item.ticketId)}
                              className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 transition cursor-pointer"
                            >
                              Mark Resolved
                            </button>
                          )}
                        </div>
                      </div>

                      <p className="text-xs text-neutral-300 whitespace-pre-wrap bg-neutral-950/60 p-3 rounded-xl border border-neutral-800/60">
                        {item.message}
                      </p>

                      <div className="flex items-center gap-4 text-[11px] text-neutral-400 pt-1">
                        <span>
                          Sender: <strong className="text-neutral-200">{item.name || 'Anonymous'}</strong>
                        </span>
                        <span>•</span>
                        <span>
                          Email: <strong className="text-neutral-200">{item.email}</strong>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: GA4 & TELEMETRY */}
          {activeTab === 'analytics' && (
            <div className="space-y-4">
              <div className="bg-neutral-900/60 p-5 rounded-2xl border border-neutral-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                      <Activity className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-white">Google Analytics 4 Measurement Stream</h2>
                      <p className="text-xs text-neutral-400">Active Web Stream: zipytiny (www.zipytiny.app)</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/20">
                      G-L5JRKLMZLV
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-xs">
                  <div className="bg-neutral-950 p-3 rounded-xl border border-neutral-800">
                    <p className="text-neutral-400 text-[11px]">Measurement ID</p>
                    <p className="text-white font-mono font-bold mt-1">G-L5JRKLMZLV</p>
                  </div>
                  <div className="bg-neutral-950 p-3 rounded-xl border border-neutral-800">
                    <p className="text-neutral-400 text-[11px]">Stream ID</p>
                    <p className="text-white font-mono font-bold mt-1">15116778246</p>
                  </div>
                  <div className="bg-neutral-950 p-3 rounded-xl border border-neutral-800">
                    <p className="text-neutral-400 text-[11px]">Google Ads Remarketing</p>
                    <p className="text-white font-mono font-bold mt-1">AW-969986458</p>
                  </div>
                </div>
              </div>

              {/* In-Memory Event Feed */}
              <div className="bg-neutral-900/60 p-4 rounded-2xl border border-neutral-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                      Client Event Stream (Recent Session Activity)
                    </h3>
                    <p className="text-[11px] text-neutral-400">
                      Live events fired by this browser session into the GA4 dataLayer
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      clearSessionEvents();
                      setSessionEvents([]);
                    }}
                    className="text-xs text-neutral-400 hover:text-rose-400 transition"
                  >
                    Clear Feed
                  </button>
                </div>

                {sessionEvents.length === 0 ? (
                  <div className="p-6 text-center text-xs text-neutral-500 bg-neutral-950/60 rounded-xl border border-neutral-800">
                    No client events captured yet. Navigate around pages to see live tracking events.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {sessionEvents.map((evt, idx) => (
                      <div
                        key={evt.id || idx}
                        className="bg-neutral-950 p-3 rounded-xl border border-neutral-800/80 flex items-start justify-between text-xs"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-blue-400">{evt.name}</span>
                            <span className="text-[10px] text-neutral-500">{evt.timestamp}</span>
                          </div>
                          {evt.params && Object.keys(evt.params).length > 0 && (
                            <pre className="text-[10px] text-neutral-400 font-mono overflow-x-auto bg-neutral-900/60 p-1.5 rounded">
                              {JSON.stringify(evt.params, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: AUDIT LEDGER */}
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <div className="bg-neutral-900/60 p-4 rounded-2xl border border-neutral-800">
                <h2 className="text-base font-bold text-white">Security Audit Trail</h2>
                <p className="text-xs text-neutral-400">
                  Immutable security events, admin logins, rate-limit triggers, and lockout blocks
                </p>
              </div>

              {auditLogs.length === 0 ? (
                <div className="p-8 text-center text-neutral-500 bg-neutral-900/40 rounded-2xl border border-neutral-800">
                  No security audit events recorded.
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map(log => (
                    <div
                      key={log.id}
                      className="bg-neutral-900/60 p-3.5 rounded-xl border border-neutral-800 flex items-start justify-between text-xs"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                              log.status === 'SUCCESS'
                                ? 'bg-emerald-500/20 text-emerald-300'
                                : log.status === 'BLOCKED'
                                ? 'bg-rose-500/20 text-rose-300'
                                : 'bg-amber-500/20 text-amber-300'
                            }`}
                          >
                            {log.status}
                          </span>
                          <span className="font-bold text-white">{log.event}</span>
                          <span className="text-[11px] text-neutral-500">{log.ip}</span>
                        </div>
                        <p className="text-neutral-300 text-xs">{log.details}</p>
                      </div>
                      <span className="text-[11px] text-neutral-500 shrink-0">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 5: DIAGNOSTICS & SYSTEM */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Firestore Connection Diagnostic */}
              <div className="bg-neutral-900/60 p-5 rounded-2xl border border-neutral-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-blue-400" />
                    <div>
                      <h3 className="text-sm font-bold text-white">Database & Firestore Connection Test</h3>
                      <p className="text-xs text-neutral-400">
                        Test read/write permissions directly against the Firebase project
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleRunDbDiag}
                    disabled={dbDiagLoading}
                    className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition cursor-pointer disabled:opacity-50"
                  >
                    {dbDiagLoading ? 'Testing...' : 'Run Test'}
                  </button>
                </div>

                {dbDiagResult && (
                  <div
                    className={`p-3.5 rounded-xl text-xs font-mono border ${
                      dbDiagResult.success
                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
                        : 'bg-rose-500/10 border-rose-500/20 text-rose-300'
                    }`}
                  >
                    {JSON.stringify(dbDiagResult, null, 2)}
                  </div>
                )}
              </div>

              {/* SMTP Email Dispatcher Test */}
              <div className="bg-neutral-900/60 p-5 rounded-2xl border border-neutral-800 space-y-3">
                <div className="flex items-center gap-3">
                  <Send className="w-5 h-5 text-amber-400" />
                  <div>
                    <h3 className="text-sm font-bold text-white">SMTP Email Transmission Test</h3>
                    <p className="text-xs text-neutral-400">
                      Dispatches a live test email through your Zoho SMTP credentials
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSendTestEmail} className="flex gap-2">
                  <input
                    type="email"
                    value={testEmailInput}
                    onChange={e => setTestEmailInput(e.target.value)}
                    placeholder="Enter recipient email (e.g. bahirathan@zipytiny.app)..."
                    className="flex-1 px-3.5 py-2 rounded-xl bg-neutral-950 border border-neutral-700 text-xs text-white focus:outline-none focus:border-blue-500"
                    required
                  />
                  <button
                    type="submit"
                    disabled={testEmailLoading}
                    className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold transition cursor-pointer disabled:opacity-50"
                  >
                    {testEmailLoading ? 'Dispatching...' : 'Send Test'}
                  </button>
                </form>

                {testEmailStatus && (
                  <p className="text-xs text-neutral-300 bg-neutral-950 p-2.5 rounded-xl border border-neutral-800">
                    {testEmailStatus}
                  </p>
                )}
              </div>

              {/* Update Credentials */}
              <div className="bg-neutral-900/60 p-5 rounded-2xl border border-neutral-800 space-y-3">
                <div className="flex items-center gap-3">
                  <Key className="w-5 h-5 text-purple-400" />
                  <div>
                    <h3 className="text-sm font-bold text-white">Update Admin Credentials</h3>
                    <p className="text-xs text-neutral-400">
                      Override environment variables and save new administrative login to database
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSaveCredentials} className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newAdminUser}
                      onChange={e => setNewAdminUser(e.target.value)}
                      placeholder="New Admin Username"
                      className="px-3.5 py-2 rounded-xl bg-neutral-950 border border-neutral-700 text-xs text-white focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="password"
                      value={newAdminPass}
                      onChange={e => setNewAdminPass(e.target.value)}
                      placeholder="New Admin Password"
                      className="px-3.5 py-2 rounded-xl bg-neutral-950 border border-neutral-700 text-xs text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold transition cursor-pointer"
                  >
                    Save New Credentials
                  </button>
                </form>

                {credSaveStatus && (
                  <p className="text-xs text-neutral-300 bg-neutral-950 p-2.5 rounded-xl border border-neutral-800">
                    {credSaveStatus}
                  </p>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
